import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Header from "./landing/Header"; // Assuming Header is in this path
import {
  FaRegClock,
  FaLanguage,
  FaUser,
  FaStar,
  FaStarHalfAlt,
  FaRegComments,
  FaUserMd,
  FaAward,
  FaFileAlt,
  FaGraduationCap,
  FaCertificate,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import moment from "moment";
import { 
  userAPI, 
  availabilityAPI, 
  savePendingBooking, 
  canProceedWithBooking,
  createBookingData,
  bookingAPI
} from "../services/api";
import { tokenManager } from "../services/tokenManager";
import CommunicationModePopup from "../components/CommunicationModePopup";

function SlotsSkeleton() {
  return (
    <div className="max-h-96 overflow-y-auto animate-pulse">
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            {[...Array(7)].map((_, i) => (
              <th key={i} className="px-2 py-2 text-center">
                <div className="h-4 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                <div className="h-5 bg-gray-200 rounded w-6 mx-auto"></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(5)].map((_, rowIdx) => (
            <tr key={rowIdx}>
              {[...Array(7)].map((_, colIdx) => (
                <td key={colIdx} className="px-2 py-2 text-center">
                  <div className="h-6 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const getLanguages = (doctor) => {
  const langs = (doctor.about && doctor.about.languages) || doctor.languages;
  if (Array.isArray(langs)) return langs.join(", ");
  if (typeof langs === "string") return langs;
  return "";
};

const DoctorDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("About");
  const [currentWeek, setCurrentWeek] = useState(moment().startOf("week"));
  const [days, setDays] = useState([]);
  const [availableSlots, setAvailableSlots] = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(null);
  
  // Communication mode popup state
  const [showCommunicationModePopup, setShowCommunicationModePopup] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  const handleGoBack = () => {
    navigate(-1);
  };

  // Debug: Log initial state
  useEffect(() => {
    console.log('🚀 Component mounted with initial state:', {
      loading,
      error,
      doctor: !!doctor,
      id
    });
  }, []);

  useEffect(() => {
    const fetchDoctorDetails = async () => {
      try {
        console.log('🔄 Starting to fetch doctor details for ID:', id);
        setLoading(true);
        setError(null);
        
        // Add a minimum loading time to ensure content loader is visible
        const startTime = Date.now();
        const minLoadingTime = 2000; // 2 seconds minimum for better visibility
        
        const response = await userAPI.getDoctorById(id);
        console.log('✅ Doctor details response:', response);
        
        if (!response || !response.doctor) {
          throw new Error('Invalid response from API - no doctor data found');
        }
        
        const doctorData = response.doctor;

        // Parse about data if it's a string
        let aboutData = doctorData.about;
        if (typeof aboutData === 'string') {
          try {
            aboutData = JSON.parse(aboutData);
          } catch (e) {
            aboutData = {};
          }
        }

        // Combine API data with fallback data to match component structure
        const fullDoctorDetails = {
          ...doctorData, // API data: _id, name, email, role, profileImage, about
          image: doctorData.profileImage
            ? (doctorData.profileImage.startsWith('https')
              ? doctorData.profileImage
              : `https://backend.adhdrapidcare.com${doctorData.profileImage}`)
            : "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80",
          degrees: `${doctorData.role === "psychiatrist"
              ? "Psychiatrist"
              : "General Practitioner, MBBS"
            }`,
          experience: aboutData?.experience || "Experience not specified",
          languages: aboutData?.languages || "English",
          gender: aboutData?.gender || "Not specified",
          rating: 4.8,
          reviews: 32,
          sessions: "1000+",
          professionalSummary: aboutData?.professionalSummary || `Dr. ${doctorData.name} is an experienced ${doctorData.role} with a focus on patient care and treatment.`,
          educationAndCertifications: aboutData?.education ? [
            {
              title: aboutData.education,
              institution: "Medical University",
            }
          ] : [
            {
              title: "MBBS, FRACGP",
              institution: "Medical University",
            },
            {
              title: "Board Certification",
              institution: "General Practice Board",
            },
          ],
          areasOfExpertise: aboutData?.areasOfExpertise
            ? aboutData.areasOfExpertise.split(',').map(area => area.trim())
            : [
              "Mental Health",
              "ADHD Assessment",
              "Psychiatric Evaluation",
              "Treatment Planning",
              "Patient Care",
              "Therapeutic Support",
            ],
          specializations: aboutData?.specializations
            ? aboutData.specializations.split(',').map(spec => spec.trim())
            : [
              "Psychiatry",
              "Mental Health",
              "ADHD Treatment",
              "Patient Care",
            ],
          certifications: aboutData?.certifications
            ? aboutData.certifications.split(',').map(cert => cert.trim())
            : [
              "Board Certification in Psychiatry",
              "Medical License",
              "Professional Membership",
            ],
        };

        console.log('✅ Setting doctor data:', fullDoctorDetails);
        setDoctor(fullDoctorDetails);
        
        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < minLoadingTime) {
          await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime));
        }
        
      } catch (err) {
        console.error('❌ Error fetching doctor details:', err);
        setError("Failed to fetch doctor details.");
      } finally {
        console.log('✅ Setting loading to false');
        setLoading(false);
      }
    };

    if (id) {
      fetchDoctorDetails();
    }
  }, [id]);

  useEffect(() => {
    const weekStart = currentWeek;
    const weekDays = [];
    for (let i = 0; i <= 6; i++) {
      weekDays.push(moment(weekStart).add(i, "days"));
    }
    setDays(weekDays);
  }, [currentWeek]);

  // Fetch all week slots at once
  useEffect(() => {
    const fetchWeekSlots = async () => {
      setSlotsLoading(true);
      setSlotsError(null);
      setAvailableSlots({});
      try {
        if (days.length && id) {
          const dateFrom = days[0].format("YYYY-MM-DD");
          const dateTo = days[6].format("YYYY-MM-DD");
          const response = await availabilityAPI.getAvailableSlotsForRange(id, dateFrom, dateTo);
          setAvailableSlots(response.slotsByDate || {});
        }
      } catch (err) {
        setSlotsError("Failed to fetch available slots. Please try again.");
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchWeekSlots();
  }, [days, id]);

  const handlePrevWeek = () => {
    setCurrentWeek(currentWeek.clone().subtract(1, "week"));
  };

  const handleNextWeek = () => {
    setCurrentWeek(currentWeek.clone().add(1, "week"));
  };

  // Helper function to check if date is before today
  const isDateBeforeToday = (date) => {
    return date.isBefore(moment(), "day");
  };

  // Build a week grid of all slots
  const buildWeekSlotsGrid = (days, slotsByDate) => {
    const allTimes = new Set();
    const filteredSlotsByDate = {};
    
    days.forEach(day => {
      const dateStr = day.format('YYYY-MM-DD');
      const isPastDate = isDateBeforeToday(day);
      
      if (!isPastDate) {
        // Only include slots for future dates
        filteredSlotsByDate[dateStr] = slotsByDate[dateStr] || [];
        (slotsByDate[dateStr] || []).forEach(slot => {
          allTimes.add(slot.start);
        });
      } else {
        // For past dates, set empty array
        filteredSlotsByDate[dateStr] = [];
      }
    });
    
    const sortedTimes = Array.from(allTimes).sort((a, b) => {
      const aStart = moment(a, 'HH:mm');
      const bStart = moment(b, 'HH:mm');
      return aStart.diff(bStart);
    });
    
    return { sortedTimes, slotsByDate: filteredSlotsByDate };
  };
  const { sortedTimes, slotsByDate: weekSlots } = buildWeekSlotsGrid(days, availableSlots);

  // Booking handler
  const handleTimeSelect = async (date, start, end) => {
    // Additional safety check to prevent booking past dates
    const selectedDate = moment(date);
    if (isDateBeforeToday(selectedDate)) {
      alert('Cannot book appointments for past dates.');
      return;
    }
    
    const user = tokenManager.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

    // Set the selected time slot and show communication mode popup
    setSelectedTimeSlot({ date, start, end });
    setShowCommunicationModePopup(true);
  };

  const handleCommunicationModeConfirm = async (sessionMode) => {
    if (!selectedTimeSlot) return;
    
    const { date, start, end } = selectedTimeSlot;
    const user = tokenManager.getUser();
    
    try {
      setBookingLoading(date + start);
      const bookingData = {
        doctorId: id,
        patientId: user.id || user._id,
        date,
        startTime: start,
        endTime: end,
        sessionMode: sessionMode
      };
      const response = await bookingAPI.bookSlot(bookingData);
      setBookingLoading(null);
      if (response && response.session && response.session.id) {
        navigate(`/success?session_id=${response.session.id}`);
      } else {
        alert('Booking failed. Please try again.');
      }
    } catch (err) {
      setBookingLoading(null);
      alert('Booking failed. Please try again.');
    }
  };

  const times = ["09:00 AM", "10:30 AM", "02:00 PM", "03:30 PM"];

  // Debug logging
  console.log('🔍 Current state:', { loading, error, doctor: !!doctor, id });

  // Test loading state - you can remove this after testing
  const testLoading = () => {
    setLoading(true);
    setDoctor(null);
    setError(null);
    setTimeout(() => {
      setLoading(false);
      // Reload the page to restore data
      window.location.reload();
    }, 3000);
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Doctor Details Skeleton */}
            <div className="w-full max-w-lg space-y-8 max-h-screen overflow-y-auto scrollbar-hide">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200">
                    <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="h-28 w-28 rounded-full bg-gray-200 animate-pulse mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded animate-pulse mb-2 w-40"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mb-2"></div>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-sm">
                    <div className="h-6 bg-blue-100 rounded-full animate-pulse w-32"></div>
                    <div className="h-6 bg-blue-100 rounded-full animate-pulse w-24"></div>
                    <div className="h-6 bg-blue-100 rounded-full animate-pulse w-20"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
                <div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-48 mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-4/6"></div>
                  </div>
                </div>
                <div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-56 mb-4"></div>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-gray-200 rounded animate-pulse mr-4"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-40"></div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-6 h-6 bg-gray-200 rounded animate-pulse mr-4"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-36 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-44"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse w-40 mb-4"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <div key={item} className="flex items-center">
                        <div className="w-5 h-5 bg-gray-200 rounded animate-pulse mr-3"></div>
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {/* Right: Booking/Calendar Skeleton */}
            <div className="flex-1 lg:sticky lg:top-8 lg:self-start max-h-screen overflow-y-auto">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="h-6 bg-gray-200 rounded animate-pulse w-40 mb-4"></div>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-9 h-9 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                  <div className="w-9 h-9 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        {[...Array(7)].map((_, i) => (
                          <th key={i} className="px-2 py-2 text-center text-xs font-semibold border-b">
                            <div className="h-4 bg-gray-200 rounded w-8 mx-auto mb-1"></div>
                            <div className="h-5 bg-gray-200 rounded w-6 mx-auto"></div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(3)].map((_, rowIdx) => (
                        <tr key={rowIdx}>
                          {[...Array(7)].map((_, colIdx) => (
                            <td key={colIdx} className="px-2 py-2 text-center">
                              <div className="h-6 bg-gray-200 rounded w-full"></div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4 text-xs text-gray-400 text-center">
                    <span className="inline-flex items-center">
                      <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                      Loading available slots...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Doctor not found.</p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 overflow-hidden">
      {/* <Header /> */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Doctor Details */}
          <div className="w-full max-w-lg space-y-8 max-h-screen overflow-y-auto scrollbar-hide">
            {/* Doctor Info */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="mb-4">
                <button
                  onClick={handleGoBack}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col items-center">
                <img
                  className="h-28 w-28 rounded-full mb-4"
                  src={doctor.image}
                  alt={doctor.name}
                />
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">
                  {doctor.name}
                </h1>
                <p className="text-gray-700 mt-1 text-center">
                  {doctor.degrees}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-3 text-sm text-gray-800">
                  <span className="flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    <FaRegClock className="mr-2" />
                    {(doctor.about && doctor.about.experience) || doctor.experience}
                  </span>
                  <span className="flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    <FaLanguage className="mr-2" />
                    {getLanguages(doctor)}
                  </span>
                  <span className="flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    <FaUser className="mr-2" />
                    {(doctor.about && doctor.about.gender) || doctor.gender}
                  </span>
                </div>
              </div>
            </div>
            {/* Professional Summary, Education, Expertise, Specializations, Certifications */}
            <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Professional Summary</h3>
                <p className="mt-2 text-gray-600">
                  {(doctor.about && doctor.about.professionalSummary) || doctor.professionalSummary}
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Education & Certifications</h3>
                <ul className="mt-4 space-y-4">
                  {doctor.educationAndCertifications && doctor.educationAndCertifications.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 pt-1">
                        {item.title.includes("Board") ? (
                          <FaCertificate className="text-blue-600 text-2xl" />
                        ) : (
                          <FaGraduationCap className="text-blue-600 text-2xl" />
                        )}
                      </div>
                      <div className="ml-4">
                        <p className="font-semibold text-gray-800">{item.title}</p>
                        <p className="text-gray-600">{item.institution}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Areas of Expertise</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-4">
                  {(doctor.about && doctor.about.areasOfExpertise
                    ? doctor.about.areasOfExpertise.split(',').map((area) => area.trim())
                    : doctor.areasOfExpertise
                  ).map((area, idx) => (
                    <div key={idx} className="flex items-start">
                      <FaCheckCircle className="text-blue-600 mr-2 flex-shrink-0 pt-1" size={18} />
                      <span className="text-gray-800 leading-tight">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Specializations</h3>
                <div className="space-y-3">
                  {(doctor.about && doctor.about.specializations
                    ? doctor.about.specializations.split(',').map((spec) => spec.trim())
                    : doctor.specializations
                  ).map((spec, idx) => (
                    <div key={idx} className="flex items-center">
                      <FaAward className="text-blue-600 mr-2" />
                      <span className="text-gray-800">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h3>
                <div className="space-y-3">
                  {(doctor.about && doctor.about.certifications
                    ? doctor.about.certifications.split(',').map((cert) => cert.trim())
                    : doctor.certifications
                  ).map((cert, idx) => (
                    <div key={idx} className="flex items-center">
                      <FaFileAlt className="text-blue-600 mr-2" />
                      <span className="text-gray-800">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* Right: Week Calendar Grid */}
          <div className="flex-1 lg:sticky lg:top-8 lg:self-start max-h-screen overflow-y-auto ">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Book Appointment</h3>
              <div className="flex items-center justify-between mb-4">
                {!currentWeek.isSame(moment().startOf("week"), "week") && !currentWeek.isBefore(moment().startOf("week")) ? (
                  <button
                    onClick={handlePrevWeek}
                    className="p-2 rounded-full hover:bg-gray-100"
                  >
                    <FaChevronLeft />
                  </button>
                ) : (
                  <div className="w-9 h-9" />
                )}
                <div className="text-center font-semibold">
                  {/* Calendar header: show both months if week spans two months */}
                  {(() => {
                    const firstDay = days[0];
                    const lastDay = days[days.length - 1];
                    if (!firstDay || !lastDay) return currentWeek.format("MMMM YYYY");
                    const firstMonth = firstDay.format("MMMM");
                    const lastMonth = lastDay.format("MMMM");
                    const firstYear = firstDay.format("YYYY");
                    const lastYear = lastDay.format("YYYY");
                    if (firstMonth === lastMonth && firstYear === lastYear) {
                      return `${firstMonth} ${firstYear}`;
                    } else if (firstYear === lastYear) {
                      return `${firstMonth} - ${lastMonth} ${firstYear}`;
                    } else {
                      return `${firstMonth} ${firstYear} - ${lastMonth} ${lastYear}`;
                    }
                  })()}
                </div>
                <button
                  onClick={handleNextWeek}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <FaChevronRight />
                </button>
              </div>
              {/* Week Calendar Grid */}
              <div className="overflow-x-auto">
                {slotsLoading ? (
                  <SlotsSkeleton />
                ) : slotsError ? (
                  <div className="text-center text-red-600 py-8">{slotsError}</div>
                ) : (
                  <div className="overflow-y-auto">
                    <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        {days.map((day) => {
                          const dateStr = day.format('YYYY-MM-DD');
                          const isPastDate = isDateBeforeToday(day);
                          return (
                            <th key={dateStr} className={`px-2 py-2 text-center text-xs font-semibold border-b ${
                              isPastDate ? 'text-gray-400' : 'text-gray-700'
                            }`}>
                              <div>{day.format('ddd')}</div>
                              <div className={`font-bold text-base ${
                                isPastDate ? 'text-gray-400' : 'text-gray-900'
                              }`}>{day.format('D')}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                        {/* Find all unique times for the week, sort them */}
                        {(() => {
                          // Collect all unique times from all slots in the week
                          const allTimesSet = new Set();
                          days.forEach(day => {
                            const dateStr = day.format('YYYY-MM-DD');
                            (weekSlots[dateStr] || []).forEach(slot => {
                              allTimesSet.add(slot.start);
                            });
                          });
                          // Sort times
                          const allTimes = Array.from(allTimesSet).sort((a, b) => {
                            const aStart = moment(a, ['HH:mm', 'hh:mm A']);
                            const bStart = moment(b, ['HH:mm', 'hh:mm A']);
                            return aStart.diff(bStart);
                          });
                          // Render a row for each time
                          return allTimes.map(time => (
                          <tr key={time}>
                            {days.map(day => {
                              const dateStr = day.format('YYYY-MM-DD');
                              const isPastDate = isDateBeforeToday(day);
                              // Find slot for this day/time
                              const slot = (weekSlots[dateStr] || []).find(s => s.start === time);
                              return (
                                <td key={dateStr} className="px-2 py-2 text-center">
                                  {isPastDate ? (
                                    <span className="text-gray-300 text-xs">-</span>
                                  ) : slot ? (
                                    <button
                                      onClick={() => handleTimeSelect(dateStr, slot.start, slot.end)}
                                      className="bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded px-2 py-1 text-xs w-full flex items-center justify-center"
                                      disabled={!!bookingLoading}
                                    >
                                      {bookingLoading === dateStr + slot.start ? (
                                        <svg className="animate-spin h-4 w-4 mr-1 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                      ) : null}
                                      {bookingLoading === dateStr + slot.start ? 'Booking...' : slot.start}
                                    </button>
                                  ) : (
                                    <span className="text-gray-300 text-xs">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                        })()}
                    </tbody>
                  </table>
                  </div>
                )}
                {/* Note about past dates */}
                <div className="mt-4 text-xs text-gray-500 text-center">
                  <span className="inline-flex items-center">
                    <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                    Past dates are not available for booking
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Communication Mode Popup */}
      <CommunicationModePopup
        isOpen={showCommunicationModePopup}
        onClose={() => setShowCommunicationModePopup(false)}
        onConfirm={handleCommunicationModeConfirm}
        selectedTime={selectedTimeSlot?.start}
        selectedDate={selectedTimeSlot?.date}
      />
    </div>
  );
};

export default DoctorDetails;
